import {
    AuditOutlined,
    AppstoreAddOutlined,
    AppstoreOutlined,
    BarcodeOutlined,
    BellOutlined,
    CloudServerOutlined,
    CommentOutlined,
    ControlOutlined,
    CrownOutlined,
    DashboardOutlined,
    EyeOutlined,
    FileTextOutlined,
    GiftOutlined,
    HomeOutlined,
    MessageOutlined,
    QrcodeOutlined,
    QuestionCircleOutlined,
    RollbackOutlined,
    SafetyOutlined,
    SettingOutlined,
    ShoppingOutlined,
    SwapOutlined,
    TabletOutlined,
    TeamOutlined,
    ToolOutlined,
    UserOutlined,
    WalletOutlined
} from '@ant-design/icons';

/**
 * 菜单分组配置工厂函数
 */
export const getMenuGroups = () => [
  {
    key: 'core',
    title: '核心业务',
    items: [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: '仪表板',
      },
      {
        key: '/device',
        icon: <TabletOutlined />,
        label: '设备管理',
        requiredPermissions: ['device:read'],
      },
      {
        key: '/device-reset',
        icon: <QrcodeOutlined />,
        label: '设备重置',
        requiredPermissions: ['device:reset'],
      },
      {
        key: '/diagnostics/device-binding',
        icon: <ToolOutlined />,
        label: '绑定诊断',
        requiredPermissions: ['user:read', 'family:read', 'device:read', 'audit:log_read'],
      },
      {
        key: '/family',
        icon: <HomeOutlined />,
        label: '家庭管理',
        requiredPermissions: ['family:read'],
      },
      {
        key: '/cloud',
        icon: <CloudServerOutlined />,
        label: '家庭云盘',
        requiredPermissions: ['cloud:stats', 'cloud:audit', 'cloud:quota', 'cloud:read', 'cloud:delete', 'content:read', 'family:read'],
        children: [
          { key: '/cloud/stats', label: '统计概览', requiredPermissions: ['cloud:stats'] },
          { key: '/cloud/review', label: '资源审核', requiredPermissions: ['cloud:audit'] },
          { key: '/cloud/quotas', label: '配额管理', requiredPermissions: ['cloud:quota'] },
          { key: '/cloud/resources', label: '资源列表', requiredPermissions: ['cloud:read'] },
          { key: '/cloud/trash', label: '回收站', requiredPermissions: ['cloud:delete'] },
          { key: '/cloud/xunting', label: '熏听管理', requiredPermissions: ['content:read'] },
          { key: '/cloud/voice-clone', label: '声音克隆', requiredPermissions: ['family:read'] },
          { key: '/cloud/synthesis-tasks', label: 'AI 故事合成', requiredPermissions: ['cloud:read'] },
          { key: '/cloud/story-library', label: '平台故事库', requiredPermissions: ['cloud:read'] },
        ],
      },
    ],
  },
  {
    key: 'user-ops',
    title: '用户与增长',
    items: [
      {
        key: '/users',
        icon: <UserOutlined />,
        label: '用户管理',
        requiredPermissions: ['user:read'],
      },
      {
        key: '/invitation',
        icon: <GiftOutlined />,
        label: '邀请管理',
        requiredPermissions: ['invitation:read', 'invitation:rule:manage'],
        children: [
          { key: '/invitation/stats', label: '邀请统计', requiredPermissions: ['invitation:read'] },
          { key: '/invitation/records', label: '邀请记录', requiredPermissions: ['invitation:read'] },
          { key: '/invitation/rewards', label: '奖励记录', requiredPermissions: ['invitation:read'] },
          { key: '/invitation/rules', label: '奖励规则', requiredPermissions: ['invitation:rule:manage'] },
        ],
      },
      {
        key: '/marketing',
        icon: <SettingOutlined />,
        label: '营销活动',
        requiredPermissions: ['marketing:manage', 'marketing:read', 'banner:read'],
        children: [
          { key: '/marketing/configs', label: '参数配置', requiredPermissions: ['marketing:manage'] },
          // 活动入口统一由首页 Banner 承担，「营销入口」宫格已退役
          { key: '/banner', label: '首页Banner', requiredPermissions: ['banner:read'] },
          { key: '/marketing/campaign-h5', label: '活动详情', requiredPermissions: ['marketing:read'] },
          { key: '/marketing/campaign-forms', label: '互动问卷/调研', requiredPermissions: ['marketing:read'] },
          { key: '/marketing/checkin-rules', label: '签到规则', requiredPermissions: ['marketing:read'] },
          { key: '/marketing/audit-log', label: '变更日志', requiredPermissions: ['marketing:read'] },
          { key: '/campaign/free-month', label: '会员赠送', requiredPermissions: ['marketing:read'] },
        ],
      },
    ],
  },
  {
    key: 'transaction',
    title: '交易与会员',
    items: [
      {
        key: '/payment',
        icon: <WalletOutlined />,
        label: '支付管理',
        requiredPermissions: ['stats:payment_read', 'order:read'],
        children: [
          { key: '/payment/overview', label: '支付概览', requiredPermissions: ['stats:payment_read'] },
          { key: '/payment/orders', label: '订单管理', requiredPermissions: ['order:read'] },
          { key: '/payment/refunds', label: '退款管理', requiredPermissions: ['order:read'] },
        ],
      },
      {
        key: '/wallet-group',
        icon: <WalletOutlined />,
        label: '钱包管理',
        requiredPermissions: ['wallet:read', 'wallet:manage'],
        children: [
          { key: '/wallet', label: '家庭钱包', requiredPermissions: ['wallet:read'] },
          { key: '/wallet/points-guide', label: '积分说明配置', requiredPermissions: ['wallet:manage'] },
        ],
      },
      {
        key: '/membership',
        icon: <CrownOutlined />,
        label: '会员管理',
        requiredPermissions: ['membership:quota:read', 'subscription:read'],
        children: [
          { key: '/membership/quotas', label: '配额配置', requiredPermissions: ['membership:quota:read'] },
          { key: '/membership/subscriptions', label: '订阅管理', requiredPermissions: ['subscription:read'] },
          { key: '/membership/entitlements', label: '权益链路', requiredPermissions: ['membership:quota:read'] },
        ],
      },
      {
        key: '/exchange',
        icon: <SwapOutlined />,
        label: '换货管理',
        requiredPermissions: ['order:read'],
      },
      {
        key: '/returns',
        icon: <RollbackOutlined />,
        label: '退货管理',
        requiredPermissions: ['order:read'],
      },
      {
        key: '/products',
        icon: <ShoppingOutlined />,
        label: '套餐管理',
        requiredPermissions: ['product:read'],
      },
      {
        key: '/redemption',
        icon: <BarcodeOutlined />,
        label: '兑换码管理',
        requiredPermissions: ['redemption:read'],
        children: [
          { key: '/redemption/batches', label: '批次管理', requiredPermissions: ['redemption:read'] },
          { key: '/redemption/codes', label: '兑换码列表', requiredPermissions: ['redemption:read'] },
          { key: '/redemption/stats', label: '兑换统计', requiredPermissions: ['redemption:read'] },
        ],
      },
    ],
  },
  {
    key: 'content',
    title: '内容与客服',
    items: [
      {
        key: '/faq',
        icon: <QuestionCircleOutlined />,
        label: 'FAQ管理',
        requiredPermissions: ['faq:read'],
      },
      {
        key: '/policy',
        icon: <FileTextOutlined />,
        label: '协议管理',
        requiredPermissions: ['policy:read'],
      },
      {
        key: '/notification',
        icon: <BellOutlined />,
        label: '通知管理',
        requiredPermissions: ['notification:read'],
      },
      {
        key: '/notification-audience',
        icon: <TeamOutlined />,
        label: '消息受众',
        requiredPermissions: ['notification:read'],
      },
      {
        key: '/feedback',
        icon: <CommentOutlined />,
        label: '反馈管理',
        requiredPermissions: ['feedback:read'],
      },
    ],
  },
  {
    key: 'system',
    title: '系统与安全',
    items: [
      {
        key: '/staff',
        icon: <TeamOutlined />,
        label: '管理员管理',
        requiredPermissions: ['permission:read'],
      },
      {
        key: '/roles',
        icon: <SafetyOutlined />,
        label: '角色权限',
        requiredPermissions: ['role:manage'],
      },
      {
        key: '/version',
        icon: <AppstoreAddOutlined />,
        label: '版本管理',
        requiredPermissions: ['version:read'],
      },
      {
        key: '/store-config',
        icon: <AppstoreOutlined />,
        label: '商店配置',
        requiredPermissions: ['version:read'],
      },
      {
        key: '/feature-permissions',
        icon: <ControlOutlined />,
        label: '功能权限',
        requiredPermissions: ['feature_permission:read'],
      },
      {
        key: '/feature-visibility',
        icon: <EyeOutlined />,
        label: '特性可见性',
        requiredPermissions: ['feature_visibility:read'],
      },
      {
        key: '/system/audit-logs',
        icon: <AuditOutlined />,
        label: '审计日志',
        requiredPermissions: ['audit:log_read'],
      },
      {
        key: '/sms',
        icon: <MessageOutlined />,
        label: '短信管理',
        requiredPermissions: ['sms:read'],
      },
    ],
  },
  {
    key: 'platform',
    title: '平台与开发',
    items: [
      {
        key: '/platform',
        icon: <CloudServerOutlined />,
        label: '中台管理',
        requiredPermissions: ['dashboard:read'],
        children: [
          { key: '/platform/pricing', label: '定价配置', requiredPermissions: ['dashboard:read'] },
        ],
      },
      {
        key: '/dev-tools',
        icon: <ToolOutlined />,
        label: '开发者工具',
        requiredPermissions: ['system:config_manage'],
      },
    ],
  },
];

export const menuGroups = getMenuGroups();

/**
 * 获取所有菜单项的扁平列表（用于搜索）
 * @returns {Array} 扁平化的菜单项数组
 */
export const getAllMenuItems = () => {
  const items = [];
  getMenuGroups().forEach(group => {
    group.items.forEach(item => {
      items.push({ ...item, groupKey: group.key });
      if (item.children) {
        item.children.forEach(child => {
          items.push({ ...child, parentKey: item.key, groupKey: group.key });
        });
      }
    });
  });
  return items;
};

export default getMenuGroups;  // eslint-disable-line
